/*!

=========================================================
* Light Bootstrap Dashboard React - v2.0.1
=========================================================

* Product Page: https://www.creative-tim.com/product/light-bootstrap-dashboard-react
* Copyright 2022 Creative Tim (https://www.creative-tim.com)
* Licensed under MIT (https://github.com/creativetimofficial/light-bootstrap-dashboard-react/blob/master/LICENSE.md)

* Coded by Creative Tim

=========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.

*/
import Listing from "views/Listing";
import Collection from "views/Collection";
import CollectionDetail from "views/CollectionDetail";
import View3D from "views/View3D";
import Shop from "views/Shop";
import ShopSold from "views/ShopSold";
import Bot from "views/Bot";
import Dashboard from "views/Dashboard.js";
import UserProfile from "views/UserProfile.js";
import TableList from "views/TableList.js";
import Typography from "views/Typography.js";
import Icons from "views/Icons.js";
import Maps from "views/Maps.js";
import Notifications from "views/Notifications.js";
import Upgrade from "views/Upgrade.js";
import View3DLamp from "views/View3DLamp";
import BoxMaker from "views/BoxMaker";
import AutoP2P from "views/AutoP2P";
import K3PLChat from "views/K3PLChat";
import ProjectList from "views/K3Project/ProjectList";
import CommitList from "views/K3Commit/CommitList";
import CommitView from "views/K3Commit/CommitView";
import { getTranslate, LANGUAGE_KEYS } from "utils";
import K3DocList from 'views/K3Doc/K3DocList';
import K3DocView from 'views/K3Doc/K3DocView';
import K3ScriptList from './views/K3Script/K3ScriptList';
import K3ScriptCreator from './views/K3Script/K3ScriptCreator';
import K3ScriptView from './views/K3Script/K3ScriptView';
import K3AgentList from 'views/K3Agent/K3AgentList';
import K3AgentView from 'views/K3Agent/K3AgentView';
import K3TaskList from 'views/K3Task/K3TaskList';
import K3TaskView from 'views/K3Task/K3TaskView';
import K3TaskPlayground from 'views/K3Task/K3TaskPlayground';
import K3ConversationList from "views/K3Conversation/K3ConversationList";
import K3ConversationView from "views/K3Conversation/K3ConversationView";
import PermissionList from './views/PermissionManagement/PermissionList';
import UserGroupList from './views/PermissionManagement/UserGroupList';
import UserList from './views/PermissionManagement/UserList';
import MyAccount from './views/PermissionManagement/MyAccount';
import NewVersionNotification from './views/K3Doc/NewVersionNotification';
import { connect } from 'react-redux';
import UIConfigList from './views/UIConfig/UIConfigList';
import FlowList from './views/K3Flow/FlowList';
import FlowViewOld from './views/K3Flow/FlowViewOld';
import FlowView from './views/K3Flow/FlowView';

// Import Tabler Icons
import {
  IconAd,
  IconChartPie,
  IconShoppingCart,
  IconTruckDelivery,
  IconBuildingFactory2,
  IconTag,
  IconHeart,
  IconRobot,
  IconDashboard,
  IconUser,
  IconTable,
  IconTypography,
  IconAtom2,
  IconLayoutGrid,
  IconBox,
  IconLamp,
  IconList,
  IconClockHour4,
  IconScan,
  IconListDetails,
  IconPlus,
  IconEdit,
  IconBell,
  IconMessage,
  IconStack,
  IconPuzzle,
  IconBrandAndroid,
  IconNotes,
  IconUpload,
  IconCurrencyDollar
} from "@tabler/icons-react";

// User Groups Enum
export const USER_GROUPS = {
  ROOT: 'Root',
  ADMIN: 'Admin',
  RND: 'Team RnD',
  SALE: 'Team Sale',
  FULFILL: 'Team Fulfill',
  FACTORY: 'Team Factory'
};

// Route Access Levels
export const ROUTE_ACCESS = {
  ROOT_ONLY: [USER_GROUPS.ROOT],
  ADMIN_ONLY: [USER_GROUPS.ROOT, USER_GROUPS.ADMIN],
  RND: [USER_GROUPS.ROOT, USER_GROUPS.ADMIN, USER_GROUPS.RND],
  SALE: [USER_GROUPS.ROOT, USER_GROUPS.ADMIN, USER_GROUPS.SALE],
  FULFILL: [USER_GROUPS.ROOT, USER_GROUPS.ADMIN, USER_GROUPS.FULFILL],
  FACTORY: [USER_GROUPS.ROOT, USER_GROUPS.ADMIN, USER_GROUPS.FACTORY],
  ALL_USERS: [USER_GROUPS.ROOT, USER_GROUPS.ADMIN, USER_GROUPS.RND, USER_GROUPS.SALE, USER_GROUPS.FULFILL, USER_GROUPS.FACTORY]
};

// View Flags
export const VIEW_FLAGS = {
  LOCALHOST: window.location.hostname.includes("localhost"),
  EIT: true,
  FORCE_VIEW_ECOM: true
};

// Helper function to check if user has access to a route
export const hasAccess = (route, userGroups) => {
  return true;
  // If no allowedUserGroups specified, allow access
  if (!route.allowedUserGroups) return true;
  
  // If user has no groups, deny access
  if (!userGroups || userGroups.length === 0) return false;
  
  // Check if user has at least one of the allowed groups
  return userGroups.some(group => route.allowedUserGroups.includes(group));
};

const dashboardRoutes = [

  {
    path: "/k3pl-chat",
    name: "Chat",
    icon: IconMessage,
    component: K3PLChat,
    layout: "/v1",
    allowedUserGroups: ROUTE_ACCESS.ALL_USERS
  },
  {
    path: "/k3pl-conversation/:id",
    name: "Conversation",
    icon: IconMessage,
    component: K3ConversationView,
    layout: "/v1",
    hidden: true,
    allowedUserGroups: ROUTE_ACCESS.ALL_USERS
  },
  {
    path: "/k3pl-conversation",
    name: "Conversation",
    icon: IconStack,
    component: K3ConversationList,
    layout: "/v1",
    allowedUserGroups: ROUTE_ACCESS.ALL_USERS
  },
  {
    path: "/project",
    name: "Project",
    icon: IconPuzzle,
    component: FlowList,
    layout: "/v1",
    allowedUserGroups: ROUTE_ACCESS.ALL_USERS
  },
  // ========
  {
    path: '/k3agent/:id',
    name: 'K3AgentView',
    component: K3AgentView,
    layout: "/v1",
    hidden: true,
    allowedUserGroups: ROUTE_ACCESS.ALL_USERS
  },
  {
    path: '/k3agent',
    name: 'Agent',
    icon: IconBrandAndroid,
    component: K3AgentList,
    layout: '/v1',
    topLine: true,
    allowedUserGroups: ROUTE_ACCESS.ALL_USERS,
    children: [
      {
        path: '/k3agent',
        name: 'All',
        component: K3AgentList,
        layout: '/v1',
        allowedUserGroups: ROUTE_ACCESS.ALL_USERS
      },
      {
        path: '/k3agent?main_type=HUMAN',
        name: 'Human',
        component: K3AgentList,
        layout: '/v1',
        allowedUserGroups: ROUTE_ACCESS.ALL_USERS
      },
      {
        path: '/k3agent?main_type=AI',
        name: 'AI',
        component: K3AgentList,
        layout: '/v1',
        allowedUserGroups: ROUTE_ACCESS.ALL_USERS
      },
      {
        path: '/k3agent?main_type=MACHINE',
        name: 'Machine',
        component: K3AgentList,
        layout: '/v1',
        allowedUserGroups: ROUTE_ACCESS.ALL_USERS
      }
    ]
  },
  {
    path: '/k3doc/:id',
    name: 'K3DocView',
    component: K3DocView,
    layout: "/v1",
    hidden: true,
    allowedUserGroups: ROUTE_ACCESS.ALL_USERS
  },
  {
    path: '/k3doc',
    name: 'Doc',
    icon: IconListDetails,
    component: K3DocList,
    layout: '/v1',
    allowedUserGroups: ROUTE_ACCESS.ALL_USERS,
    children: [
      {
        path: '/k3doc',
        name: 'All',
        component: K3DocList,
        layout: '/v1',
        allowedUserGroups: ROUTE_ACCESS.ALL_USERS
      },
      {
        path: '/k3doc?type=system_prompt',
        name: 'System Prompt',
        component: K3DocList,
        layout: '/v1',
        allowedUserGroups: ROUTE_ACCESS.ALL_USERS
      },
      {
        path: '/k3doc?type=main_message',
        name: 'Main Message',
        component: K3DocList,
        layout: '/v1',
        allowedUserGroups: ROUTE_ACCESS.ALL_USERS,
        hidden: true
      },
      {
        path: '/k3doc?type=sub_message',
        name: 'Sub Message',
        component: K3DocList,
        layout: '/v1',
        allowedUserGroups: ROUTE_ACCESS.ALL_USERS,
        hidden: true
      },
      {
        path: '/k3doc?type=user_data',
        name: 'User Data',
        component: K3DocList,
        layout: '/v1',
        allowedUserGroups: ROUTE_ACCESS.ALL_USERS
      },
      {
        path: '/k3doc?type=agent_data',
        name: 'Agent Data',
        component: K3DocList,
        layout: '/v1',
        allowedUserGroups: ROUTE_ACCESS.ALL_USERS
      },
      {
        path: '/k3doc?type=flow',
        name: 'Flow',
        component: K3DocList,
        layout: '/v1',
        allowedUserGroups: ROUTE_ACCESS.ALL_USERS
      },
      {
        path: '/k3doc?type=k3_document',
        name: 'K3 Document',
        component: K3DocList,
        layout: '/v1',
        allowedUserGroups: ROUTE_ACCESS.ALL_USERS
      },
      {
        path: '/k3doc?type=k3package_v1.0',
        name: 'K3Package v1.0',
        component: K3DocList,
        layout: '/v1',
        allowedUserGroups: ROUTE_ACCESS.ALL_USERS
      },
      {
        path: '/k3doc?type=k3package_v1.0_block',
        name: 'K3Package v1.0 Block',
        component: K3DocList,
        layout: '/v1',
        allowedUserGroups: ROUTE_ACCESS.ALL_USERS
      }
    ]
  },
  {
    path: '/k3script/:id',
    name: 'Script',
    component: K3ScriptView,
    layout: "/v1",
    hidden: true,
    allowedUserGroups: ROUTE_ACCESS.ALL_USERS
  },
  {
    path: '/k3script',
    name: 'Script',
    icon: IconNotes,
    component: K3ScriptList,
    layout: '/v1',
    allowedUserGroups: ROUTE_ACCESS.ALL_USERS,
    children: [
      {
        path: '/k3script',
        name: 'All',
        component: K3ScriptList,
        layout: '/v1',
        allowedUserGroups: ROUTE_ACCESS.ALL_USERS
      },
      {
        path: '/k3script?script_type=script_runner',
        name: 'Script Runner',
        component: K3ScriptList,
        layout: '/v1',
        allowedUserGroups: ROUTE_ACCESS.ALL_USERS
      },
      {
        path: '/k3script?script_type=web_agent_main_script',
        name: 'Web Agent Main Script',
        component: K3ScriptList,
        layout: '/v1',
        allowedUserGroups: ROUTE_ACCESS.ALL_USERS
      },
      {
        path: '/k3script?script_type=web_agent_sub_script',
        name: 'Web Agent Sub Script',
        component: K3ScriptList,
        layout: '/v1',
        allowedUserGroups: ROUTE_ACCESS.ALL_USERS
      },
      {
        path: '/k3script?script_type=general_script',
        name: 'General Script',
        component: K3ScriptList,
        layout: '/v1',
        allowedUserGroups: ROUTE_ACCESS.ALL_USERS
      }
    ]
  },
  {
    path: '/k3task/:id',
    name: 'K3TaskView',
    component: K3TaskView,
    layout: "/v1",
    hidden: true,
    allowedUserGroups: ROUTE_ACCESS.ALL_USERS
  },
  {
    path: '/k3task',
    name: 'Task',
    icon: IconList,
    component: K3TaskList,
    layout: "/v1",
    allowedUserGroups: ROUTE_ACCESS.ALL_USERS,
    children: [
      {
        path: '/k3task',
        name: 'All',
        component: K3TaskList,
        layout: '/v1',
        allowedUserGroups: ROUTE_ACCESS.ALL_USERS
      },
      {
        path: '/k3task?type=script_runner',
        name: 'Script Runner',
        component: K3TaskList,
        layout: '/v1',
        allowedUserGroups: ROUTE_ACCESS.ALL_USERS
      },
      {
        path: '/k3task?type=ai_chat',
        name: 'AI Chat',
        component: K3TaskList,
        layout: '/v1',
        allowedUserGroups: ROUTE_ACCESS.ALL_USERS
      },
      {
        path: '/k3task?type=ai_helper',
        name: 'AI Helper',
        component: K3TaskList,
        layout: '/v1',
        allowedUserGroups: ROUTE_ACCESS.ALL_USERS
      }
    ]
  },
  // ========
  {
    path: '/playground',
    name: 'Playground',
    icon: IconAtom2,
    component: K3TaskPlayground,
    layout: "/v1",
    topLine: true,
    allowedUserGroups: ROUTE_ACCESS.ALL_USERS
  },
  {
    path: '/flow',
    name: 'Flow',
    icon: IconStack,
    layout: '/v1',
    allowedUserGroups: ROUTE_ACCESS.ALL_USERS,
    children: [
      {
        path: '/flow/list',
        name: 'Flow List',
        icon: IconList,
        component: FlowList,
        layout: '/v1',
        allowedUserGroups: ROUTE_ACCESS.ALL_USERS
      },
      // {
      //   path: '/flow/old',
      //   name: 'FlowViewOld',
      //   component: FlowViewOld,
      //   layout: '/v1',
      //   hidden: true,
      //   allowedUserGroups: ROUTE_ACCESS.ALL_USERS
      // },
      {
        path: '/flow/view',
        name: 'FlowView',
        component: FlowView,
        layout: '/v1',
        hidden: true,
        allowedUserGroups: ROUTE_ACCESS.ALL_USERS
      }
    ]
  },
  {
    path: "/commit/:id",
    name: "Commit",
    icon: IconPuzzle,
    component: CommitView,
    layout: "/v1",
    hidden: true,
    allowedUserGroups: ROUTE_ACCESS.ALL_USERS
  },
  {
    path: "/commit",
    name: "Commit",
    icon: IconUpload,
    component: CommitList,
    layout: "/v1",
    allowedUserGroups: ROUTE_ACCESS.ALL_USERS
  },
  {
    path: "/collection/:id",
    name: getTranslate(LANGUAGE_KEYS.TEXT_COLLECTION_DETAIL),
    icon: IconHeart,
    component: K3PLChat,
    layout: "/v1",
    hidden: true,
    allowedUserGroups: ROUTE_ACCESS.ROOT_ONLY
  },
  {
    path: "/collection",
    name: getTranslate(LANGUAGE_KEYS.TEXT_COLLECTION),
    icon: IconHeart,
    component: Collection,
    layout: "/v1",
    hidden: false,
    allowedUserGroups: ROUTE_ACCESS.ROOT_ONLY
  },
  //=========
  {
    path: "/permission-management",
    name: "Settings",
    icon: IconAtom2,
    layout: "/v1",
    topLine: true,
    allowedUserGroups: ROUTE_ACCESS.ADMIN_ONLY,
    children: [
      {
        path: "/permission-management/permissions",
        name: "Permissions",
        icon: IconList,
        component: PermissionList,
        layout: "/v1",
        allowedUserGroups: ROUTE_ACCESS.ADMIN_ONLY
      },
      {
        path: "/permission-management/groups",
        name: "User Groups",
        icon: IconList,
        component: UserGroupList,
        layout: "/v1",
        allowedUserGroups: ROUTE_ACCESS.ADMIN_ONLY
      },
      {
        path: "/permission-management/users",
        name: "Users",
        icon: IconList,
        component: UserList,
        layout: "/v1",
        allowedUserGroups: ROUTE_ACCESS.ADMIN_ONLY
      },
      {
        path: '/version-notification',
        name: 'Version Mới',
        icon: IconBell,
        component: NewVersionNotification,
        layout: "/v1",
        allowedUserGroups: ROUTE_ACCESS.ALL_USERS
      },
      {
        path: '/ui-config',
        name: 'UI Config',
        icon: IconLayoutGrid,
        component: UIConfigList,
        layout: "/v1",
        allowedUserGroups: ROUTE_ACCESS.ADMIN_ONLY
      },
      {
        path: "/my-account",
        name: "My Account",
        icon: IconUser,
        component: MyAccount,
        layout: "/v1",
        allowedUserGroups: ROUTE_ACCESS.ALL_USERS
      }
    ]
  },
  {
    path: "/bot",
    name: getTranslate(LANGUAGE_KEYS.TEXT_BOT),
    icon: IconRobot,
    component: Bot,
    layout: "/v1",
    hidden: true,
    allowedUserGroups: ROUTE_ACCESS.ROOT_ONLY
  },
  {
    path: "/dashboard",
    name: getTranslate(LANGUAGE_KEYS.TEXT_DASHBOARD),
    icon: IconDashboard,
    component: Dashboard,
    layout: "/v1",
    hidden: true,
    allowedUserGroups: ROUTE_ACCESS.ALL_USERS
  },
  {
    path: "/user",
    name: getTranslate(LANGUAGE_KEYS.TEXT_USER_PROFILE),
    icon: IconUser,
    component: UserProfile,
    layout: "/v1",
    hidden: true,
    allowedUserGroups: ROUTE_ACCESS.ALL_USERS
  },
  {
    path: "/table",
    name: "Table List",
    icon: IconTable,
    component: TableList,
    layout: "/v1",
    hidden: true,
    allowedUserGroups: ROUTE_ACCESS.ALL_USERS
  },
  {
    path: "/typography",
    name: "Typography",
    icon: IconTypography,
    component: Typography,
    layout: "/v1",
    hidden: true,
    allowedUserGroups: ROUTE_ACCESS.ALL_USERS
  },
  {
    path: "/demo",
    name: "Demo",
    icon: IconAtom2,
    // component: FlowDemoXY,
    layout: "/v1",
    allowedUserGroups: ROUTE_ACCESS.ALL_USERS,
    children: [
    ]
  }
];

// Create a connected version of the routes that filters based on user groups
const mapStateToProps = (state) => ({
  userGroups: state.authenReducer.user?.group_ids || []
});

const getFilteredRoutes = (userGroups) => {
  return dashboardRoutes.map(route => {
    // If route has children, filter them too
    if (route.children) {
      return {
        ...route,
        children: route.children.filter(child => hasAccess(child, userGroups))
      };
    }
    return route;
  }).filter(route => hasAccess(route, userGroups));
};

// Export the raw routes for testing and other purposes
export { dashboardRoutes };

// Export the routes array directly
export default dashboardRoutes;
